import { GuidedSetup } from './guided-setup';
import { useEffect, useState } from 'react';
import { ArrowRight, Clock } from 'lucide-react';
import type { OemContext } from '../../lib/auth';
import type { OemWorkspace } from '../../lib/oem-client';
import { displayValue } from '../../lib/oem-display';
import { dailyTasks, deadlineText } from './task-dashboard-model';
import { customerTimeline } from './milestone-timeline-model';
import { homeActions } from './workspace-model';
import { WorkspaceIcon } from './workspace-icons';
import { OwnerSummary } from './owner-summary';
import { oemBundle } from '@sqi/shared-schemas';
export function WorkspaceHome({
  context,
  workspace,
  link,
}: {
  context: OemContext;
  workspace: OemWorkspace;
  link: (key: string) => string;
}) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const refresh = () => setNow(Date.now());
    const visible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const timer = setInterval(refresh, 60000);
    document.addEventListener('visibilitychange', visible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', visible);
    };
  }, []);
  const tasks = dailyTasks(
    workspace.records.filter((r) => context.readBundles.includes(oemBundle(r.kind))),
    now,
  );
  const timeline =
    context.audience === 'customer'
      ? customerTimeline({
          ...workspace,
          records: workspace.records.filter((r) => context.readBundles.includes(oemBundle(r.kind))),
        })
      : null;
  return (
    <>
      <div className="simple-home-title">
        <h1>{context.audience === 'customer' ? 'โครงการของคุณ' : 'วันนี้จะทำอะไร?'}</h1>
        <p>{workspace.project.label}</p>
      </div>
      <GuidedSetup context={context} link={link} />
      <OwnerSummary context={context} workspace={workspace} now={now} link={link} />
      <div className="workspace-actions">
        {homeActions(context).map((action) => (
          <a className="workspace-tile" key={action.page} href={link(action.page)}>
            <span className="workspace-icon">
              <WorkspaceIcon name={action.icon} />
            </span>
            <strong>{action.label}</strong>
          </a>
        ))}
      </div>
      {timeline ? (
        <section className="card customer-journey" aria-label="ขั้นตอนของโครงการ">
          <h2>เริ่มจากสิ่งที่อยากทำ</h2>
          <ol>
            <li>
              <a href={link('brief')}>เลือกแนวคิดและบอกความต้องการ</a>
              <p>บันทึกร่างก่อน แล้วส่งให้ทีมงานเมื่อพร้อม</p>
            </li>
            <li>
              ดูตัวอย่างและแบบที่ทีมงานแชร์<p>เปิดฉบับที่ต้องการตอบกลับจากรายการด้านล่าง</p>
            </li>
            <li>
              ทบทวนข้อเสนอและเอกสาร<p>ตรวจเงื่อนไขที่ได้รับก่อนยืนยันตามสิทธิ์</p>
            </li>
          </ol>
          {timeline.stages
            .flatMap((stage) => stage.entries)
            .filter((entry) => ['sample', 'artwork'].includes(entry.kind) && entry.route)
            .slice(0, 4)
            .map((entry) => (
              <a
                className="journey-record"
                key={entry.id}
                href={link(entry.route?.path ?? 'work-list')}
              >
                <WorkspaceIcon name={entry.kind === 'sample' ? 'sample' : 'package'} />
                <span>
                  {entry.label} · v{entry.version}
                  <small>{entry.route?.label}</small>
                </span>
                <ArrowRight aria-hidden="true" />
              </a>
            ))}
        </section>
      ) : null}
      <section className="card compact-followup" aria-label="งานที่ต้องติดตาม">
        <div className="between">
          <h2>{timeline ? 'ติดตามโครงการ' : 'งานที่ต้องติดตาม'}</h2>
          <a href={link('work-list')} className="text-button">
            ดูงานทั้งหมด <ArrowRight aria-hidden="true" />
          </a>
        </div>
        {timeline ? (
          <>
            <p>มี {timeline.total} รายการที่แชร์ให้คุณดู</p>
            <p className="muted">
              {timeline.frozen
                ? 'มีข้อมูลที่ยืนยันไว้ แยกจากร่างล่าสุด'
                : 'ยังไม่มีข้อมูลการยืนยันที่แชร์ให้คุณดู'}
            </p>
          </>
        ) : tasks.length ? (
          <ul>
            {tasks.slice(0, 3).map((task) => (
              <li key={task.record.id}>
                <a href={link('tasks')}>
                  <strong>{displayValue(task.record.content.label ?? 'งานที่ยังไม่มีชื่อ')}</strong>
                </a>
                <p>
                  {displayValue(
                    task.record.content.nextAction ?? 'เปิดงานเพื่อดูรายละเอียดและผู้รับผิดชอบ',
                  )}
                </p>
                <span className="muted">
                  <Clock aria-hidden="true" />
                  {deadlineText(task.deadline)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p>ยังไม่พบงานในขอบเขตนี้</p>
        )}
        <details className="home-scope">
          <summary>ขอบเขตของข้อมูล</summary>
          <p className="scope">
            ข้อมูลที่ยังขาดต้องตรวจสอบเพิ่มเติม · ไม่ใช่การอนุมัติหรือความพร้อมผลิต
          </p>
        </details>
      </section>
    </>
  );
}
